import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Volume2, VolumeX } from "lucide-react";
import { createMysticMusic } from "@/mysticAudio";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { DreamButton, PaymentResult } from "@/DreamCheckout";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import BuyersPage from "@/BuyersPage";
import { trackVisit } from "@/track";

const VIDEO_URL =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_633f99d4-12b9-42a5-90f2-619c23fa9c66/artifacts/89c6a69dacdfef6b_V9Compress_145050.mp4";
const INTERLUDE_MS = 2000;

const STARS = Array.from({ length: 60 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 2 + 0.6,
  delay: Math.random() * 6,
  dur: 3 + Math.random() * 5,
}));

const SHOOTING_STARS = [
  { top: 8, left: 70, delay: 1, dur: 7 },
  { top: 22, left: 92, delay: 4.5, dur: 9 },
  { top: 3, left: 45, delay: 8, dur: 11 },
  { top: 35, left: 80, delay: 12, dur: 10 },
  { top: 15, left: 30, delay: 15.5, dur: 12 },
];

const ShootingStars = () => (
  <div className="shooting-stars" data-testid="shooting-stars">
    {SHOOTING_STARS.map((s, i) => (
      <span
        key={i}
        className="shooting-star"
        style={{ top: `${s.top}%`, left: `${s.left}%`, animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s` }}
      />
    ))}
  </div>
);

const SquareCompass = ({ variant = "" }) => (
  <svg className={`square-compass ${variant}`} viewBox="0 0 200 200" data-testid={variant ? `freemason-sign-${variant}` : "freemason-sign"} aria-hidden="true">
    <g className="sc-compass">
      <circle cx="100" cy="28" r="9" />
      <path d="M95 36 L42 168 M105 36 L158 168" />
      <path d="M42 168 l-4 12 M158 168 l4 12" />
    </g>
    <path className="sc-square" d="M30 108 L100 176 L170 108" />
    <path className="sc-square-inner" d="M44 108 L100 162 L156 108" />
    <text x="100" y="122" textAnchor="middle" className="sc-g">G</text>
  </svg>
);

const MysticBackground = ({ charging }) => (
  <div className="mystic-bg" data-testid="mystic-background">
    <div className="nebula nebula-a" />
    <div className="nebula nebula-b" />
    <div className="nebula nebula-c" />
    <AllSeeingEye variant="bg" />
    <SquareCompass variant="bg" />
    <div className="stars">
      {STARS.map((s) => (
        <span
          key={s.id}
          className="star"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
          }}
        />
      ))}
    </div>
    <ShootingStars />
    <div className="mist" />
    <div className={`core-glow ${charging ? "charging" : ""}`} />
    <div className="grain" />
  </div>
);

const AllSeeingEye = ({ variant = "", ...rest }) => (
  <svg className={`all-seeing-eye ${variant}`} viewBox="0 0 200 200" data-testid={variant ? `all-seeing-eye-${variant}` : "all-seeing-eye"} aria-hidden="true" {...rest}>
    <g className="eye-rays">
      {Array.from({ length: 24 }, (_, i) => (
        <line key={i} x1="100" y1="112" x2="100" y2="8" transform={`rotate(${i * 15} 100 112)`} />
      ))}
    </g>
    <polygon points="100,30 182,170 18,170" className="eye-triangle" />
    <path d="M50 122 Q100 72 150 122 Q100 162 50 122 Z" className="eye-lid" />
    <circle cx="100" cy="120" r="17" className="eye-iris" />
    <circle cx="100" cy="120" r="7" className="eye-pupil" />
    <circle cx="94" cy="114" r="3" className="eye-glint" />
  </svg>
);

function MysticReveal() {
  const [stage, setStage] = useState("enter"); // enter | waiting | video | interlude
  const [muted, setMuted] = useState(false);
  const peakRef = useRef(null);

  // The intro eye is brightest halfway through its 6s glow.
  const chimeAtPeak = () => {
    clearTimeout(peakRef.current);
    peakRef.current = setTimeout(() => {
      const m = musicRef.current;
      if (m?.isRunning()) m.chime();
    }, 3000);
  };
  useEffect(() => () => clearTimeout(peakRef.current), []);
  const [buffering, setBuffering] = useState(false);
  const musicRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const chimeRef = useRef(null);

  const [soundLocked, setSoundLocked] = useState(false);

  useEffect(() => { trackVisit("open"); }, []);

  useEffect(() => {
    const music = createMysticMusic();
    musicRef.current = music;
    music.start();
    const check = setTimeout(() => setSoundLocked(!music.isRunning()), 600);
    const unlock = () => music.unlock().then(() => setSoundLocked(false)).catch(() => {});
    const events = ["pointerdown", "touchstart", "keydown"];
    events.forEach((e) => window.addEventListener(e, unlock, { once: true, passive: true }));
    return () => {
      clearTimeout(check);
      events.forEach((e) => window.removeEventListener(e, unlock));
      music.close();
      musicRef.current = null;
    };
  }, []);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    [].concat(chimeRef.current).forEach(clearTimeout);
  }, []);

  const revealVideo = () => {
    setStage("video");
    musicRef.current?.fadeOut(2);
    const v = videoRef.current;
    v.currentTime = 0;
    v.muted = muted;
    v.volume = 1;
    v.play().catch(() => {
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    });
  };

  const handleEnter = () => {
    clearTimeout(peakRef.current);
    trackVisit("enter");
    const root = document.documentElement;
    (root.requestFullscreen || root.webkitRequestFullscreen)?.call(root)?.catch?.(() => {});
    if (!muted) musicRef.current?.unlock();
    revealVideo();
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (videoRef.current) videoRef.current.muted = next;
    musicRef.current?.setMuted(next);
  };

  const replayLoop = () => {
    setStage("interlude");
    chimeRef.current = setTimeout(() => musicRef.current?.chime(), INTERLUDE_MS / 2);
    timerRef.current = setTimeout(() => {
      setStage("video");
      const v = videoRef.current;
      v.currentTime = 0;
      v.play().catch(() => {});
    }, INTERLUDE_MS);
  };

  const showVideo = stage === "video";

  return (
    <main className={`stage ${stage === "video" ? "video-on" : ""} ${stage === "enter" ? "on-enter" : ""}`} data-testid="mystic-stage">
      <MysticBackground charging={stage === "waiting"} />

      {stage === "enter" && <AllSeeingEye variant="intro" onAnimationStart={chimeAtPeak} onAnimationIteration={chimeAtPeak} />}
      {stage === "enter" && (
        <button className="enter-btn" onClick={handleEnter} data-testid="enter-button">
          <span className="enter-ring" />
          <span className="enter-text">Enter</span>
        </button>
      )}
      {stage === "enter" && (
        <p className="sound-hint" data-testid="sound-hint">Touch anywhere to make your dreams come true</p>
      )}

      {stage === "waiting" && <AllSeeingEye />}
      {stage === "interlude" && <AllSeeingEye variant="interlude" />}
      {stage === "video" && <AllSeeingEye variant="overlay" />}
      {stage === "video" && <SquareCompass />}
      {stage === "video" && buffering && <div className="video-loader" data-testid="video-loading-spinner" />}

      <div className="reveal-column">
        <div className={`video-wrap ${showVideo ? "visible" : ""}`} data-testid="video-container">
          <video
            ref={videoRef}
            src={VIDEO_URL}
            playsInline
            poster="/video-poster.jpg"
            preload="auto"
            className="reveal-video"
            onEnded={replayLoop}
            onWaiting={() => setBuffering(true)}
            onPlaying={() => setBuffering(false)}
            data-testid="reveal-video"
          />
        </div>
        <DreamButton visible={stage === "video" || stage === "interlude"} />
      </div>

      <div className="controls">
        <button className="ctrl-btn icon" onClick={toggleMute} data-testid="mute-toggle-button" aria-label="Toggle sound">
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>
    </main>
  );
}

const ResultPage = ({ cancelled }) => (
  <main className="stage" data-testid="payment-result-stage">
    <MysticBackground charging={false} />
    <PaymentResult cancelled={cancelled} />
  </main>
);

const ClerkLayout = () => (
  <ClerkAuthProvider>
    <main className="stage auth-stage">
      <MysticBackground charging={false} />
      <div className="auth-content"><Outlet /></div>
    </main>
  </ClerkAuthProvider>
);

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MysticReveal />} />
        <Route path="/payment/success" element={<ResultPage />} />
        <Route path="/payment/cancel" element={<ResultPage cancelled />} />
        <Route element={<ClerkLayout />}>
          <Route path="/buyers" element={<BuyersPage />} />
          <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp />} />
          <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
